/**
 * Feature 100 — trial funnel strip on the admin Advisors tab.
 * Created / active / expired / converted counts + conversion rate from
 * GET /admin/advisors/trials/metrics. Hidden until at least one trial has
 * ever existed so the tab stays unchanged for teams not using trials.
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/core/api/client';
import { Card } from '@/shared/components/ui/card';

type TrialMetrics = {
  created: number;
  converted: number;
  expired: number;
  active: number;
  conversionRate: number;
};

const fetchTrialMetrics = async (): Promise<TrialMetrics> => {
  const response = await apiClient.get<TrialMetrics>('/admin/advisors/trials/metrics');
  return response.data;
};

export function TrialMetricsCard() {
  const { data } = useQuery({
    queryKey: ['admin', 'trial-metrics'],
    queryFn: fetchTrialMetrics,
    staleTime: 60_000,
  });

  if (!data || data.created === 0) {
    return null;
  }

  const stats: Array<{ label: string; value: string }> = [
    { label: 'Trials created', value: String(data.created) },
    { label: 'Active', value: String(data.active) },
    { label: 'Expired', value: String(data.expired) },
    { label: 'Converted', value: String(data.converted) },
    { label: 'Conversion rate', value: `${Math.round(data.conversionRate * 100)}%` },
  ];

  return (
    <Card className="p-4 mb-4" data-testid="trial-metrics-card">
      <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">
        Trial FP Accounts
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        {stats.map(({ label, value }) => (
          <div key={label}>
            <p className="text-2xl text-gray-900">{value}</p>
            <p className="text-xs text-gray-500">{label}</p>
          </div>
        ))}
      </div>
    </Card>
  );
}
